if (!customElements.get('raeya-media-carousel')) {
  class RaeyaMediaCarousel extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('[data-raeya-scroller]');
      this.current = this.querySelector('[data-raeya-current]');
      this.total = this.querySelector('[data-raeya-total]');
      if (!this.scroller || !this.current) return;

      this.mq = window.matchMedia('(max-width: 989px)');
      this.onScroll = this.onScroll.bind(this);
      this.onBreakpoint = this.onBreakpoint.bind(this);
      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      this.mq.addEventListener('change', this.onBreakpoint);
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!this.mq.matches || !entry.isIntersecting || entry.intersectionRatio < 0.6) return;
            const slides = Array.from(this.slides());
            const index = slides.indexOf(entry.target);
            if (index >= 0) this.setCurrent(index + 1);
          });
        },
        { root: this.scroller, threshold: 0.6 }
      );
      this.slides().forEach((slide) => this.observer.observe(slide));
      this.onActive = () => {
        if (this.desk && this.desk.matches) {
          this.syncThumbs();
          return;
        }
        if (!this.mq.matches) return;
        const active = this.scroller.querySelector('[data-raeya-slide].is-active');
        if (!active) return;
        const left =
          active.getBoundingClientRect().left -
          this.scroller.getBoundingClientRect().left +
          this.scroller.scrollLeft;
        if (Math.abs(this.scroller.scrollLeft - left) > 2) this.scroller.scrollTo({ left });
      };
      this.activeObserver = new MutationObserver(this.onActive);
      this.slides().forEach((slide) => {
        this.activeObserver.observe(slide, { attributes: true, attributeFilter: ['class'] });
      });
      this.onScroll();
      this.desk = window.matchMedia('(min-width: 990px)');
      this.onDesk = this.onDesk.bind(this);
      this.desk.addEventListener('change', this.onDesk);
      this.onDesk();
    }

    onDesk() {
      if (this.desk.matches) this.enableDesktop();
      else this.disableDesktop();
    }

    enableDesktop() {
      const slides = Array.from(this.slides());
      if (slides.length < 2 || this.thumbs) {
        this.classList.add('raeya-desk');
        this.setHeaderVar();
        return;
      }
      this.classList.add('raeya-desk');
      this.setHeaderVar();
      if (!this.scroller.querySelector('[data-raeya-slide].is-active')) slides[0].classList.add('is-active');
      const icon = (d) =>
        `<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="${d}" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      this.prevBtn = document.createElement('button');
      this.prevBtn.type = 'button';
      this.prevBtn.className = 'raeya-desk-nav raeya-desk-nav--prev';
      this.prevBtn.setAttribute('aria-label', 'Previous image');
      this.prevBtn.innerHTML = icon('M10 3 5 8l5 5');
      this.nextBtn = document.createElement('button');
      this.nextBtn.type = 'button';
      this.nextBtn.className = 'raeya-desk-nav raeya-desk-nav--next';
      this.nextBtn.setAttribute('aria-label', 'Next image');
      this.nextBtn.innerHTML = icon('M6 3l5 5-5 5');
      this.prevBtn.addEventListener('click', () => this.step(-1));
      this.nextBtn.addEventListener('click', () => this.step(1));
      const gallery = this.querySelector('media-gallery') || this;
      gallery.prepend(this.prevBtn);
      gallery.prepend(this.nextBtn);

      this.thumbs = document.createElement('ul');
      this.thumbs.className = 'raeya-thumbs';
      this.thumbs.setAttribute('aria-label', 'Product image thumbnails');
      slides.forEach((slide, i) => {
        const img = slide.querySelector('img');
        const isVideo = !!slide.querySelector('deferred-media, video, iframe');
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.dataset.index = String(i);
        btn.setAttribute('aria-label', `${isVideo ? 'Play video' : 'Show image'} ${i + 1} of ${slides.length}`);
        if (img) {
          const t = document.createElement('img');
          t.src = img.currentSrc || img.src;
          t.alt = '';
          t.loading = 'lazy';
          t.width = 64;
          t.height = 64;
          btn.appendChild(t);
        }
        if (isVideo) {
          const play = document.createElement('span');
          play.className = 'raeya-thumbs__play';
          play.setAttribute('aria-hidden', 'true');
          play.textContent = '\u25B6';
          btn.appendChild(play);
        }
        btn._slide = slide;
        btn.addEventListener('click', () => this.go(Array.from(this.slides()).indexOf(slide)));
        li.appendChild(btn);
        this.thumbs.appendChild(li);
      });
      this.appendChild(this.thumbs);

      this.onKey = (e) => {
        if (!this.desk.matches || e.altKey || e.ctrlKey || e.metaKey) return;
        const tag = (e.target.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1, true); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1, true); }
      };
      this.addEventListener('keydown', this.onKey);
      this.syncThumbs();
    }

    disableDesktop() {
      this.classList.remove('raeya-desk');
    }

    setHeaderVar() {
      const header = document.querySelector('.section-header, sticky-header, header');
      const h = header ? Math.round(header.getBoundingClientRect().height) : 100;
      this.style.setProperty('--raeya-head', `${h}px`);
    }

    activeIndex() {
      const slides = Array.from(this.slides());
      const i = slides.findIndex((s) => s.classList.contains('is-active'));
      return i < 0 ? 0 : i;
    }

    step(dir, focusThumb) {
      const n = this.slides().length;
      this.go((this.activeIndex() + dir + n) % n, focusThumb);
    }

    go(index, focusThumb) {
      const slides = Array.from(this.slides());
      const target = slides[index];
      if (!target) return;
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      slides.forEach((s) => s.classList.toggle('is-active', s === target));
      const status = this.querySelector('[id^="GalleryStatus-"]');
      if (status) status.textContent = `Image ${index + 1} of ${slides.length}`;
      this.syncThumbs();
      if (focusThumb) Array.from(this.thumbs?.querySelectorAll('button') || []).find((b) => b._slide === target)?.focus();
    }

    syncThumbs() {
      if (!this.thumbs) return;
      this.thumbs.querySelectorAll('button').forEach((b) => {
        const on = b._slide.classList.contains('is-active');
        if (on) b.setAttribute('aria-current', 'true');
        else b.removeAttribute('aria-current');
        if (on && this.desk.matches) {
          const li = b.parentElement;
          const left = li.offsetLeft - (this.thumbs.clientWidth - li.offsetWidth) / 2;
          this.thumbs.scrollTo({ left: Math.max(0, left) });
        }
      });
    }

    disconnectedCallback() {
      this.scroller?.removeEventListener('scroll', this.onScroll);
      this.mq?.removeEventListener('change', this.onBreakpoint);
      this.observer?.disconnect();
      this.activeObserver?.disconnect();
      this.desk?.removeEventListener('change', this.onDesk);
    }

    setCurrent(index) {
      const next = String(index);
      if (this.current.textContent !== next) this.current.textContent = next;
    }

    slides() {
      return this.scroller.querySelectorAll('[data-raeya-slide]');
    }

    onBreakpoint() {
      this.onScroll();
    }

    onScroll() {
      if (!this.mq.matches) return;
      const width = this.scroller.clientWidth;
      const count = this.slides().length;
      if (!width || count < 2) return;
      if (this.total) this.total.textContent = String(count);
      const index = Math.min(count, Math.max(1, Math.round(this.scroller.scrollLeft / width) + 1));
      this.setCurrent(index);
    }
  }

  customElements.define('raeya-media-carousel', RaeyaMediaCarousel);
}

(function () {
  const desk = window.matchMedia('(min-width: 990px)');
  const MAX_ROWS = 2;
  function reset(fs) {
    fs.querySelectorAll('.raeya-swatch-hidden').forEach((l) => l.classList.remove('raeya-swatch-hidden'));
    fs.querySelectorAll('.raeya-swatch-more').forEach((b) => b.remove());
  }
  function rowsOf(els) {
    const tops = [];
    els.forEach((e) => { const t = e.offsetTop; if (!tops.some((x) => Math.abs(x - t) < 4)) tops.push(t); });
    return tops.sort((a, b) => a - b);
  }
  function compact(fs) {
    reset(fs);
    if (!desk.matches || fs.dataset.raeyaExpanded === 'true') return;
    const labels = Array.from(fs.querySelectorAll('input[type="radio"] + label'));
    if (labels.length < 3) return;
    const rows = rowsOf(labels);
    if (rows.length <= MAX_ROWS) return;
    const limit = rows[MAX_ROWS - 1] + 4;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'raeya-swatch-more';
    btn.setAttribute('aria-expanded', 'false');
    fs.appendChild(btn);
    const hideable = () => labels.filter((l) => !l.classList.contains('raeya-swatch-hidden') && !l.previousElementSibling?.checked);
    labels.forEach((l) => { if (l.offsetTop > limit && !l.previousElementSibling?.checked) l.classList.add('raeya-swatch-hidden'); });
    const update = () => {
      const n = labels.filter((l) => l.classList.contains('raeya-swatch-hidden')).length;
      btn.textContent = `+${n}`;
      btn.setAttribute('aria-label', `Show ${n} more options`);
    };
    update();
    let guard = labels.length;
    while (guard-- > 0 && (btn.offsetTop > limit || labels.some((l) => !l.classList.contains('raeya-swatch-hidden') && l.offsetTop > limit))) {
      const h = hideable();
      if (!h.length) break;
      h[h.length - 1].classList.add('raeya-swatch-hidden');
      update();
    }
    btn.addEventListener('click', () => {
      fs.dataset.raeyaExpanded = 'true';
      const first = labels.find((l) => l.classList.contains('raeya-swatch-hidden'));
      reset(fs);
      first?.previousElementSibling?.focus?.();
    });
  }
  function run() {
    document.querySelectorAll('.raeya-pdp__variant fieldset').forEach(compact);
  }
  function init() {
    const root = document.querySelector('.raeya-pdp__variant');
    if (!root) return;
    let busy = false;
    const apply = () => {
      if (busy) return;
      busy = true;
      mo.disconnect();
      try { run(); } finally { mo.observe(root, { childList: true, subtree: true }); busy = false; }
    };
    const mo = new MutationObserver(() => requestAnimationFrame(apply));
    apply();
    desk.addEventListener('change', apply);
    window.addEventListener('load', apply);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
